import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { showToast } from '@klpkonsern/ui';

import {
    ActionButton,
    ActionLink,
    Block,
    ConfirmationDialog,
    Heading,
    Loader,
} from '@klp/design';
import { Main } from '@klp/layout';
import { useCurrentUser } from '@klp/auth';

import RolleSelector from './RolleSelector';
import RegionSelector from './RegionSelector';
import { UserRoutes } from '../config';
import {
    useGetBrukerQuery,
    useGetByggProsjektkontrollerListQuery,
    usePostBrukerRegionMutation,
    usePostBrukerRollerMutation,
    usePostByggProsjektkontrollerMutation,
} from '../services/rtkApi';
import { ByggProsjektkontroller } from './ByggProsjektkontroller';
import { BrukerRoller } from '../types';

const useStyles = makeStyles(() => ({
    container: { maxWidth: '600px' },
    errorMessage: { color: 'red' },
}));

export default function AdministrerBruker() {
    const { t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const { state } = useLocation<{ user: string }>();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const { isEDAdmin, currentUser } = useCurrentUser();

    const validationSchema = Yup.object().shape({
        region: Yup.string().required(t('users.editUser.regionRequired')),
    });

    const hasEditAccess =
        (currentUser?.isBrukerAdministrator || isEDAdmin) ?? false;

    const {
        data: brukerData,
        isLoading: brukerIsLoading,
        isFetching: brukerIsFetching,
    } = useGetBrukerQuery({ email: state?.user }, { skip: !state?.user });
    const { data: byggProsjektkontrollerList } =
        useGetByggProsjektkontrollerListQuery();

    const [
        postBrukerRoller,
        { isLoading: postBrukerRollerLoading, isError: postBrukerRollerError },
    ] = usePostBrukerRollerMutation();
    const [
        postBrukerRegion,
        { isLoading: postBrukerRegionLoading, isError: postBrukerRegionError },
    ] = usePostBrukerRegionMutation();
    const [
        postByggProsjektkontroller,
        {
            isLoading: postByggProsjektkontrollerLoading,
            isError: postByggProsjektkontrollerError,
        },
    ] = usePostByggProsjektkontrollerMutation();

    const handlePostBrukerRegion = async (region: string) => {
        if (!brukerData) return;

        const payload = {
            brukerId: brukerData.brukerId,
            region: region,
        };

        await postBrukerRegion(payload)
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
            });
    };

    const handlePostBrukerRoller = async (
        roller: Array<BrukerRoller>,
        region: string,
    ) => {
        if (!brukerData) return;

        const uniqueRoles = Array.from(new Set(roller));

        const brukerroller = uniqueRoles.map((rolle) => {
            const existingRole = brukerData?.roller?.find(
                (existing) => existing.rolle === rolle,
            );

            return {
                id: existingRole ? existingRole.id : undefined,
                brukerId: brukerData?.brukerId,
                rolle: rolle,
                region: existingRole?.region ? existingRole?.region : region,
            };
        });

        const payload = {
            brukerId: brukerData?.brukerId,
            roller: brukerroller,
        };

        await postBrukerRoller(payload)
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
            });
    };

    const handleSubmitByggProsjektkontroller = async (
        bpkByggAndFirma: Array<string>,
    ) => {
        if (!brukerData) return;
        setOpenConfirmationDialog(false);
        const existingByggProsjektkontrollerDict =
            (byggProsjektkontrollerList &&
                Object.fromEntries(
                    byggProsjektkontrollerList.map((bpk) => [
                        bpk.byggNummer!,
                        bpk,
                    ]),
                )) ||
            {};
        const updatedByggProsjektkontrollerList = [
            ...bpkByggAndFirma.map((nummer) =>
                existingByggProsjektkontrollerDict[nummer]
                    ? {
                          ...existingByggProsjektkontrollerDict[nummer],
                          brukerId: brukerData?.brukerId,
                      }
                    : {
                          brukerId: brukerData?.brukerId,
                          byggNummer: nummer,
                      },
            ),
        ];

        await postByggProsjektkontroller({
            brukerId: brukerData.brukerId,
            byggProsjektkontroller: updatedByggProsjektkontrollerList,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
            })
            .catch((e) => {
                showToast({
                    body: e.data
                        ? t('general.error.messageWithDetails', {
                              errorMessage: e.data,
                          })
                        : t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleSubmit = async (values: AdministrerBrukerFormValues) => {
        const regionChanged = values.region !== (brukerData?.region ?? '');
        if (regionChanged) {
            await handlePostBrukerRegion(values.region);
        }

        const existingRoles = JSON.stringify(
            brukerData?.roller.map((r) => r.rolle).sort(),
        );
        const rolesChanged =
            JSON.stringify(values.roller.sort()) !== existingRoles;
        if (rolesChanged) {
            await handlePostBrukerRoller(values.roller, values.region);
        }

        const existingByggProsjektkontroller = JSON.stringify(
            brukerData?.byggProsjektkontroller
                ?.map((bpk) => bpk.byggNummer)
                .sort(),
        );
        const byggProsjektkontrollerChanged =
            JSON.stringify(values.bpkByggAndFirma.sort()) !==
            existingByggProsjektkontroller;
        if (byggProsjektkontrollerChanged) {
            if (values.bpkAssignedByggAndFirma.length > 0) {
                setOpenConfirmationDialog(true);
                return;
            } else {
                await handleSubmitByggProsjektkontroller(
                    values.bpkByggAndFirma,
                );
            }
        }

        routeToUserList();
    };

    const routeToUserList = () => {
        if (
            postBrukerRegionError ||
            postBrukerRollerError ||
            postByggProsjektkontrollerError
        ) {
            return;
        }

        history.replace(UserRoutes.moduleRoute());
    };

    type AdministrerBrukerFormValues = {
        region: string;
        roller: Array<BrukerRoller>;
        bpkByggAndFirma: Array<string>;
        bpkAssignedByggAndFirma: Array<string>;
    };

    const initialValues: AdministrerBrukerFormValues = {
        region: brukerData?.region || '',
        roller: brukerData?.roller.map((r) => r.rolle) || [],
        bpkByggAndFirma: [],
        bpkAssignedByggAndFirma: [],
    };
    const formIsSubmitting =
        postBrukerRegionLoading ||
        postBrukerRollerLoading ||
        postByggProsjektkontrollerLoading;

    const titleTranslation = hasEditAccess
        ? 'users.editUser.titleWithName'
        : 'users.showUserInfo.secondaryTitle';

    if (!brukerData) return null;

    return (
        <Main>
            <Loader isLoading={brukerIsLoading || brukerIsFetching}>
                <Block paddingBottom="compact">
                    <ActionLink
                        actionType="linkBack"
                        translation="users.editUser.backButton"
                        href={UserRoutes.moduleRoute()}
                    />
                </Block>
                <Block paddingBottom="fat">
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation={titleTranslation}
                        translationOptions={{ name: brukerData?.brukerId }}
                    />
                </Block>

                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={(values) => handleSubmit(values)}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form className={classes.container}>
                            <Block paddingTop="tiny">
                                <RegionSelector
                                    region={values.region}
                                    hasEditAccess={hasEditAccess}
                                    brukerIsLoading={brukerIsLoading}
                                    callbackChange={(selectedRolle) =>
                                        setFieldValue('region', selectedRolle)
                                    }
                                />
                                {errors.region && touched.region && (
                                    <div className={classes.errorMessage}>
                                        {errors.region}
                                    </div>
                                )}
                            </Block>
                            <Block paddingTop="fat">
                                <RolleSelector
                                    rollerList={brukerData?.roller}
                                    hasEditAccess={hasEditAccess}
                                    brukerIsLoading={brukerIsLoading}
                                    callbackChange={(selectedRoller) =>
                                        setFieldValue('roller', selectedRoller)
                                    }
                                />
                            </Block>
                            <Block paddingTop="tiny">
                                <ByggProsjektkontroller
                                    isEnabled={values.roller?.includes(
                                        BrukerRoller.Prosjektkontroller,
                                    )}
                                    prosjektkontrollerUser={brukerData}
                                    hasEditAccess={hasEditAccess}
                                    callbackChange={(bpk, assignedBpk) => {
                                        setFieldValue('bpkByggAndFirma', bpk);
                                        setFieldValue(
                                            'bpkAssignedByggAndFirma',
                                            assignedBpk,
                                        );
                                    }}
                                />
                            </Block>
                            <Block paddingTop="fat" paddingBottom="fat">
                                <ConfirmationDialog
                                    anchorRef={anchorRef}
                                    showDialog={openConfirmationDialog}
                                    dialogContent={t(
                                        'users.bpkModal.saveCompletion',
                                        {
                                            name: brukerData?.fulltnavn,
                                            values: values?.bpkAssignedByggAndFirma,
                                        },
                                    )}
                                    onCancel={() =>
                                        setOpenConfirmationDialog(false)
                                    }
                                    onConfirm={async () => {
                                        await handleSubmitByggProsjektkontroller(
                                            values.bpkByggAndFirma,
                                        );
                                        routeToUserList();
                                    }}
                                />
                                {hasEditAccess && (
                                    <ActionButton
                                        type="submit"
                                        actionType="submit"
                                        disabled={
                                            openConfirmationDialog ||
                                            formIsSubmitting
                                        }
                                        forwardedRef={anchorRef}
                                        translation="general.save"
                                    />
                                )}
                            </Block>
                        </Form>
                    )}
                </Formik>
            </Loader>
        </Main>
    );
}
